import type { NetChannel, NetMessage, PeerId, Transport } from './transport';
import { SignalingClient } from './signalingClient';

const ICE = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] };

type MsgCb = (from: PeerId, msg: NetMessage) => void;
type PeerCb = (peerId: PeerId) => void;

interface PeerConn {
  pc: RTCPeerConnection;
  reliable: RTCDataChannel | null;
  unreliable: RTCDataChannel | null;
  makingOffer: boolean;
  polite: boolean;
}

export class WebRTCTransport implements Transport {
  public localPeerId: PeerId = '';
  public hostPeerId: PeerId = '';
  /** Stable room identity (UUID). Set at create (host) / from `joined` (client).
   *  Lets a reconnect verify it reached its own room, not a same-code stranger. */
  public roomId = '';
  private signaling: SignalingClient;
  private conns = new Map<PeerId, PeerConn>();
  private msgCbs = new Set<MsgCb>();
  private joinCbs = new Set<PeerCb>();
  private leaveCbs = new Set<PeerCb>();
  private departedCbs = new Set<(tokens: string[]) => void>();
  private runClosedCbs = new Set<() => void>();
  private errorCbs = new Set<(message: string) => void>();
  private createdCbs = new Set<(peerId: PeerId) => void>();
  private closing = false;

  constructor() {
    this.signaling = new SignalingClient({
      onCreated: (id) => {
        this.localPeerId = id;
        this.hostPeerId = id;
        this.createdCbs.forEach((cb) => cb(id));
      },
      onJoined: (id, hostId, roomId) => {
        this.localPeerId = id;
        this.hostPeerId = hostId;
        this.roomId = roomId;
        // Client initiates the connection to the host.
        this.connectTo(hostId, true, true);
      },
      onPeerJoined: (peerId) => {
        // Host waits for the client's offer; create the conn as impolite.
        this.connectTo(peerId, false, false);
      },
      onPeerLeft: (peerId) => this.dropPeer(peerId),
      onSignal: (from, data) => this.onSignal(from, data),
      onDepartures: (tokens) => this.departedCbs.forEach((cb) => cb(tokens)),
      onRunClosed: () => this.runClosedCbs.forEach((cb) => cb()),
      onError: (m) => {
        // eslint-disable-next-line no-console
        console.error('[signaling]', m);
        this.errorCbs.forEach((cb) => cb(m));
      },
    });
  }

  host(code: string, roomId: string): Promise<void> {
    this.roomId = roomId;
    return this.signaling.create(code, roomId);
  }
  joinLobby(code: string): Promise<void> {
    return this.signaling.join(code);
  }
  leaveRun(code: string, token: string): Promise<void> {
    return this.signaling.leaveRun(code, token);
  }
  endRun(code: string): void {
    this.signaling.endRun(code);
  }
  onDepartures(cb: (tokens: string[]) => void): () => void {
    this.departedCbs.add(cb);
    return () => this.departedCbs.delete(cb);
  }
  onRunClosed(cb: () => void): () => void {
    this.runClosedCbs.add(cb);
    return () => this.runClosedCbs.delete(cb);
  }
  /** Signaling-level error (e.g. join rejected: no such lobby). */
  onError(cb: (message: string) => void): () => void {
    this.errorCbs.add(cb);
    return () => this.errorCbs.delete(cb);
  }
  /** Host: the signaling server confirmed the lobby was created (vs rejecting the
   *  code as already in use). */
  onCreated(cb: (peerId: PeerId) => void): () => void {
    this.createdCbs.add(cb);
    return () => this.createdCbs.delete(cb);
  }

  private connectTo(peerId: PeerId, polite: boolean, initiator: boolean): void {
    const pc = new RTCPeerConnection(ICE);
    const conn: PeerConn = {
      pc,
      reliable: null,
      unreliable: null,
      makingOffer: false,
      polite,
    };
    this.conns.set(peerId, conn);

    pc.onicecandidate = ({ candidate }) => {
      if (candidate) {
        this.signaling.signal(peerId, { candidate });
      }
    };
    pc.onnegotiationneeded = async () => {
      try {
        conn.makingOffer = true;
        await pc.setLocalDescription();
        this.signaling.signal(peerId, { description: pc.localDescription });
      } finally {
        conn.makingOffer = false;
      }
    };
    pc.ondatachannel = ({ channel }) =>
      this.attachChannel(peerId, conn, channel);
    pc.onconnectionstatechange = () => {
      // 'failed' is terminal — a real P2P drop. Not 'disconnected' (transient ICE
      // that may recover; a true drop transitions to 'failed' after ICE timeout).
      // `closing` guards our own teardown from emitting a spurious leave.
      if (!this.closing && pc.connectionState === 'failed') {
        this.dropPeer(peerId);
      }
    };

    if (initiator) {
      // Reliable+ordered and unreliable+unordered channels.
      this.attachChannel(
        peerId,
        conn,
        pc.createDataChannel('reliable', { ordered: true }),
      );
      this.attachChannel(
        peerId,
        conn,
        pc.createDataChannel('unreliable', {
          ordered: false,
          maxRetransmits: 0,
        }),
      );
    }
  }

  private attachChannel(
    peerId: PeerId,
    conn: PeerConn,
    channel: RTCDataChannel,
  ): void {
    if (channel.label === 'reliable') {
      conn.reliable = channel;
    } else {
      conn.unreliable = channel;
    }
    channel.onmessage = (e) =>
      this.msgCbs.forEach((cb) => cb(peerId, JSON.parse(e.data as string)));
    // Fire join once per peer: only on the reliable channel opening (the
    // primary channel), so the two channels don't double-notify.
    channel.onopen = () => {
      if (channel.label === 'reliable') {
        this.joinCbs.forEach((cb) => cb(peerId));
      }
    };
  }

  private async onSignal(from: PeerId, data: unknown): Promise<void> {
    const conn = this.conns.get(from);
    if (!conn) {
      return;
    }
    const pc = conn.pc;
    const { description, candidate } = data as {
      description?: RTCSessionDescriptionInit;
      candidate?: RTCIceCandidateInit;
    };
    try {
      if (description) {
        const offerCollision =
          description.type === 'offer' &&
          (conn.makingOffer || pc.signalingState !== 'stable');
        if (offerCollision && !conn.polite) {
          return;
        } // impolite ignores
        await pc.setRemoteDescription(description);
        if (description.type === 'offer') {
          await pc.setLocalDescription();
          this.signaling.signal(from, { description: pc.localDescription });
        }
      } else if (candidate) {
        await pc.addIceCandidate(candidate).catch(() => {});
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[webrtc] signal error', err);
    }
  }

  /** Single, deduplicated peer-drop path: close + remove + notify once. A peer
   *  can drop via signaling (peer's WS closed) OR via the RTCPeerConnection
   *  failing; the `conns.has` guard makes the second one a no-op. */
  private dropPeer(peerId: PeerId): void {
    const conn = this.conns.get(peerId);
    if (!conn) {
      return;
    }
    this.conns.delete(peerId);
    conn.pc.close();
    this.leaveCbs.forEach((cb) => cb(peerId));
  }

  send(peerId: PeerId, channel: NetChannel, msg: NetMessage): void {
    const conn = this.conns.get(peerId);
    const ch = channel === 'reliable' ? conn?.reliable : conn?.unreliable;
    if (ch && ch.readyState === 'open') {
      ch.send(JSON.stringify(msg));
    }
  }
  broadcast(channel: NetChannel, msg: NetMessage): void {
    for (const peerId of this.conns.keys()) {
      this.send(peerId, channel, msg);
    }
  }
  onMessage(cb: MsgCb): () => void {
    this.msgCbs.add(cb);
    return () => this.msgCbs.delete(cb);
  }
  onPeerJoin(cb: PeerCb): () => void {
    this.joinCbs.add(cb);
    return () => this.joinCbs.delete(cb);
  }
  onPeerLeave(cb: PeerCb): () => void {
    this.leaveCbs.add(cb);
    return () => this.leaveCbs.delete(cb);
  }
  peers(): PeerId[] {
    return [...this.conns.keys()];
  }
  close(): void {
    this.closing = true;
    this.conns.forEach((c) => c.pc.close());
    this.conns.clear();
    this.signaling.close();
  }
}
